import { AppGlobalContext, AppGlobalContextType } from 'core/contexts/app.provider.context';
import { useTranslation } from 'core/i18n/i18n';
import { GraphqlError } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import _ from 'lodash';
import {
    OnChangeAutomationRuleStateData,
    Rule,
    RuleActionDataMap,
    RuleCreate,
    RuleCreateApi,
    TriggerActionDataParams,
    TriggerActionType,
} from 'modules/settings/pods/settings-automation-rules/types/automation-rules.type';
import React from 'react';
import { validate as uuidValidate } from 'uuid';
import { createRule, editRule } from '../service/settings-automation-rules.service';
import {
    AutomationRulesDataKeys,
    AutomationRulesTriggersWhen,
    RuleTrigger,
    TriggerExternalConditions,
} from '../constants/backend-enums';
import { ERRORS_MAPPINGS_AUTOMATION_RULES, TRANSL_STRG_AUT_RULES } from '../constants/common';
import { injectPropsToMatchingChildren, isRuleObjectValid, onChange } from '../constants/functions';
import { StepperOption } from '../ui-components/automation-rules-stepper';
import { TitleCardRefProps, TriggerValidationProps } from '../ui-components/title-card';
import { ActionsContainer } from './actions-container';
import { ConditionalActionsContainer } from './conditional-actions.container';
import { ConditionalTriggerContainer } from './conditional-trigger.container';
import { TriggerContainer } from './trigger-container';

interface TriggerActionsContainerProps {
    defaultOption: Rule | null;
    children: React.ReactNode;
    onCloseWithConfirmation: () => void;
    onCloseDirectly: () => void;
}

type DraftRuleAction = {
    type: '' | keyof RuleActionDataMap;
    data: Partial<RuleActionDataMap[keyof RuleActionDataMap]>;
};

export interface DraftRule extends Omit<RuleCreateApi, 'actions'> {
    actions: DraftRuleAction[];
}

type DraftRuleState = Omit<DraftRule, 'conditions' | 'actions'> & {
    conditions: (DraftRule['conditions'][number] & { customIdentifier?: string })[];
    actions: (DraftRuleAction & { customIdentifier?: string })[];
};

export const TriggerActionsContainer = (props: TriggerActionsContainerProps) => {
    const { defaultOption, onCloseWithConfirmation, onCloseDirectly, children } = props;

    const { setIsPageLoading } = React.useContext<AppGlobalContextType>(AppGlobalContext);

    const { t } = useTranslation();
    const { name, trigger, conditions, actions } = defaultOption || {};

    const [isValid, setIsValid] = React.useState(false);
    const [resetKey, setResetKey] = React.useState(0);

    const ruleNameRef = React.useRef<TitleCardRefProps>();

    const defaultRuleName = name ?? '';

    const WHEN_TRIGGER_OPTIONS = [
        {
            id: AutomationRulesTriggersWhen.operator_created,
            label: TRANSL_STRG_AUT_RULES + 'operator_created',
            value: AutomationRulesTriggersWhen.operator_created,
        },
    ];

    const defaultWhenTrigger = WHEN_TRIGGER_OPTIONS[0];
    const emptyAction = {
        type: '' as keyof RuleActionDataMap,
        data: {} as RuleActionDataMap[keyof RuleActionDataMap],
    };

    const initialValue = React.useRef<DraftRule>({
        name: defaultRuleName,
        trigger: (trigger || defaultWhenTrigger.value) as RuleTrigger,
        conditions: conditions || [],
        actions: actions || [emptyAction],
    });

    const [initialDraftState] = React.useState<DraftRuleState>(() => structuredClone(initialValue.current));
    const AutomationRulesStateRef = React.useRef<DraftRuleState>(initialDraftState);

    const onAutomationRuleChange = (data: OnChangeAutomationRuleStateData | TriggerActionDataParams) => {
        const updatedState = { ...AutomationRulesStateRef.current, ...data };
        AutomationRulesStateRef.current = updatedState;
        onChange({ ...data, state: updatedState });

        setIsValid(isRuleObjectValid(updatedState));
    };

    const onTriggerActionChange = (triggerActionData: TriggerActionType) => (data: OnChangeAutomationRuleStateData) => {
        const { name: parentArrayName } = triggerActionData;
        data.parentArrayName = parentArrayName;

        onAutomationRuleChange(data);
    };

    const onAddDeleteGlobalState = (triggerActionData: TriggerActionDataParams) => {
        onAutomationRuleChange(triggerActionData);
    };

    const checkOnCloseBehaviour = () => {
        const adaptedRule = {
            name: initialValue.current?.name,
            trigger: initialValue.current?.trigger || defaultWhenTrigger.value,
            conditions: initialValue.current?.conditions || [],
            actions: initialValue.current?.actions || [],
        };

        if (confirmationPopupCondition(adaptedRule)) {
            onCloseWithConfirmation();
        } else {
            onCloseDirectly();
        }
    };

    // Confirmation pops up when actually editing a rule, or whenever in the create screen.
    const confirmationPopupCondition = (adaptedRule: DraftRule) =>
        (!_.isEqual(AutomationRulesStateRef.current, adaptedRule) && initialValue.current !== null) ||
        initialValue.current === null;

    const onResetChangesHandler = () => {
        setResetKey((prev) => {
            AutomationRulesStateRef.current = structuredClone(initialValue.current);

            return prev + 1;
        });
    };

    const onSaveHandler = () => {
        const cleanedConditions = AutomationRulesStateRef.current.conditions
            .map((item) => {
                if (!item || typeof item !== 'object') {
                    return null;
                }

                const isCustomField = uuidValidate(item.field);

                const updated = {
                    ...item,
                    type: item.type ?? 'EQUALS',
                    ...(isCustomField && item.customIdentifier ? { identifier: item.customIdentifier } : {}),
                    ...(isCustomField ? { field: TriggerExternalConditions.custom_field } : {}),
                };

                delete updated.customIdentifier;
                return Object.keys(updated).length > 0 ? updated : null;
            })
            .filter((item) => item !== null);

        const cleanedActions = AutomationRulesStateRef.current.actions
            .map((item) => {
                if (!item || typeof item !== 'object') {
                    return null;
                }
                delete item.customIdentifier;
                return item;
            })
            .filter((item) => item !== null);

        const ruleObj = {
            conditions: cleanedConditions,
            actions: cleanedActions,
            name: AutomationRulesStateRef.current.name,
            trigger: AutomationRulesStateRef.current.trigger,
        };

        let action;
        if (defaultOption) {
            const editObj = {
                id: defaultOption.id,
                enabled: defaultOption.enabled,
                lastExecutionTime: defaultOption.lastExecutionTime,
                matches: defaultOption.matches,
                version: defaultOption.version,
            };

            action = editRule({ ...editObj, ...ruleObj } as Rule);
        } else {
            action = createRule(ruleObj as RuleCreate);
        }
        setIsPageLoading(true);
        action
            .then(() => {
                showSuccessNotification(
                    t(
                        `settings.system.automation-rules.actions.${
                            defaultOption ? 'edit' : 'create'
                        }.notification.positive`
                    )
                );
                onCloseDirectly();
            })
            .catch((err) => {
                const error = JSON.parse(err.message) as { errors: GraphqlError[] };
                const errorCode = error.errors[0].extensions.code;

                const obj: TriggerValidationProps = {
                    validity: { state: 'error', type: ['custom'] },
                    customValidationCode: ERRORS_MAPPINGS_AUTOMATION_RULES.find((el) => el.errorCode === errorCode)
                        ?.type as TriggerValidationProps['customValidationCode'],
                };

                ruleNameRef?.current?.setTriggerValidation(obj);
            })
            .finally(() => {
                setIsPageLoading(false);
            });
    };

    const triggerDefault = (
        <ConditionalTriggerContainer onChange={onTriggerActionChange({ name: AutomationRulesDataKeys.conditions })} />
    );
    const actionDefault = (
        <ConditionalActionsContainer onChange={onTriggerActionChange({ name: AutomationRulesDataKeys.actions })} />
    );

    const TriggerDefaultOptions =
        conditions?.map((condition, index: number) => ({
            content: (
                <ConditionalTriggerContainer
                    key={`condition-trigger-${condition.value}-${index}`}
                    id={index}
                    fieldsData={condition}
                    onChange={onTriggerActionChange({ name: AutomationRulesDataKeys.conditions })}
                />
            ),
            id: index,
        })) || [];

    const ActionsDefaultOptions = (actions || [emptyAction])?.map((action, index: number) => ({
        content: (
            <ConditionalActionsContainer
                key={`condition-action-${action.type}-${index}`}
                id={index}
                fieldsData={action}
                onChange={onTriggerActionChange({ name: AutomationRulesDataKeys.actions })}
            />
        ),
        id: index,
    }));

    const stepperOptions: StepperOption[] = React.useMemo(
        () => [
            {
                title: t('settings.system.automation-rules.details.trigger'),
                content: (
                    <TriggerContainer
                        key={`trigger-${resetKey}`}
                        defaultOptions={TriggerDefaultOptions}
                        whenTriggerOptions={WHEN_TRIGGER_OPTIONS}
                        defaultWhenTrigger={defaultWhenTrigger}
                        newTriggerDefault={triggerDefault}
                        onAddDeleteGlobalState={onAddDeleteGlobalState}
                        onChange={onAutomationRuleChange}
                    />
                ),
            },
            {
                title: t('settings.system.automation-rules.details.actions'),
                content: (
                    <ActionsContainer
                        key={`action-${resetKey}`}
                        defaultOptions={ActionsDefaultOptions}
                        newActionDefault={actionDefault}
                        onAddDeleteGlobalState={onAddDeleteGlobalState}
                    />
                ),
            },
        ],
        [resetKey]
    );

    const saveText = defaultOption
        ? t('settings.system.automation-rules.fields.type.save')
        : t('settings.system.automation-rules.fields.type.create');
    const headerText = defaultOption
        ? t('settings.system.automation-rules.edit.layout.title')
        : t('settings.system.automation-rules.create.layout.title');

    const injectedProps = {
        TriggerActionsLayout: {
            saveText,
            headerText,
            isValid,
            onResetChangesHandler,
            onSaveHandler,
            onClose: checkOnCloseBehaviour,
        },
        TitleCard: {
            key: resetKey,
            ref: ruleNameRef,
            name: AutomationRulesDataKeys.name,
            defaultValue: defaultRuleName,
            type: 'input',
            onChange: onAutomationRuleChange,
        },
        AutomationRulesStepper: {
            options: stepperOptions,
        },
    };

    const enhanced = injectPropsToMatchingChildren(children, injectedProps);

    return <>{enhanced}</>;
};
